import type {UnitSystem} from '@gaia/types';
import combineLineGeometry from '@gaia/utils/gaia/combineLineGeometry';
import getOSMFeatureName from '@gaia/utils/gaia/getOSMFeatureName';
import isOSMTrailSymbol from '@gaia/utils/gaia/isOSMTrailSymbol';

interface SelectOsmFeatureProps {
    feature: mapboxgl.MapboxGeoJSONFeature;
    lngLat: mapboxgl.LngLat;
    mapboxLayerId: string | undefined;
    setSelections: (popup: SelectionState[]) => void;
    querySourceFeatures?: MapQuerySourceFeatures;
    unitSystem: UnitSystem;
}

type CombinedGeometrySourceData = GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>;

export default function selectOsmFeature({
    feature,
    lngLat,
    mapboxLayerId,
    setSelections,
    querySourceFeatures,
    unitSystem,
}: SelectOsmFeatureProps) {
    const title = getOSMFeatureName(feature.properties, unitSystem);

    // Large and well-known trails have dedicated symbols that appear over and on the trail
    // This redirects clicks on symbols to select the trail
    if (querySourceFeatures && mapboxLayerId && isOSMTrailSymbol(mapboxLayerId)) {
        const sourceFeatures = querySourceFeatures('basemap__gaiaosm', {
            sourceLayer: feature.sourceLayer,
            filter: ['==', ['get', 'name'], title],
        });
        const properties = sourceFeatures[0]?.properties;

        setSelections([
            {
                type: 'osm',
                title,
                lngLat,
                properties: {
                    ...((properties as Record<string, any>) ?? {}),
                    geometry: (
                        combineLineGeometry(sourceFeatures).data as CombinedGeometrySourceData
                    ).geometry,
                },
                osmId: properties?.osm_id,
                mapboxLayerId: `${mapboxLayerId.split('__')[0]}__trail`,
            },
        ]);
    } else if (
        mapboxLayerId === `${mapboxLayerId?.split('__')[0]}__trail` // Get the appropriate GaiaTopoRasterFeet/Meter prefix
    ) {
        const sourceFeatures =
            querySourceFeatures?.('basemap__gaiaosm', {
                sourceLayer: feature.sourceLayer,
                filter: ['==', ['get', 'name'], title],
            }) ?? [];
        const properties =
            sourceFeatures.length === 0 ? feature.properties : sourceFeatures[0].properties;

        const geometry =
            sourceFeatures.length === 0
                ? feature.geometry
                : (combineLineGeometry(sourceFeatures).data as CombinedGeometrySourceData).geometry;

        setSelections([
            {
                type: 'osm',
                title,
                lngLat,
                properties: {
                    ...((properties as Record<string, any>) ?? {}),
                    geometry,
                },
                osmId: properties?.osm_id,
                mapboxLayerId,
            },
        ]);
    } else {
        setSelections([
            {
                type: 'osm',
                title,
                lngLat,
                properties: {
                    ...feature.properties,
                    geometry: feature.geometry,
                },
                osmId: feature.properties!.osm_id,
                mapboxLayerId,
            },
        ]);
    }
}
